import React, { useEffect, useMemo, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material';
import { themeCreator } from './base';
import { StylesProvider } from '@mui/styles';

export type CockpitThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  mode: CockpitThemeMode;
  setMode: (mode: CockpitThemeMode) => void;
  toggleMode: () => void;
}

export const ThemeContext = React.createContext<ThemeContextValue>({
  mode: 'light',
  setMode: () => undefined,
  toggleMode: () => undefined
});

const ThemeProviderWrapper: React.FC = (props) => {
  const storedMode = localStorage.getItem('cockpitTheme');
  const initialMode: CockpitThemeMode =
    storedMode === 'dark' || storedMode === 'light' ? storedMode : 'light';
  const [mode, setModeState] = useState<CockpitThemeMode>(initialMode);
  const baseTheme = themeCreator('PureLightTheme');
  const theme = useMemo(
    () =>
      createTheme(baseTheme, {
        typography: {
          fontFamily: "Corbel, 'Segoe UI', Arial, sans-serif",
          h1: { fontFamily: "Consolas, 'Courier New', monospace" },
          h2: { fontFamily: "Consolas, 'Courier New', monospace" },
          h3: { fontFamily: "Consolas, 'Courier New', monospace" },
          h4: { fontFamily: "Consolas, 'Courier New', monospace" },
          h5: { fontFamily: "Consolas, 'Courier New', monospace" },
          h6: { fontFamily: "Consolas, 'Courier New', monospace" }
        },
          palette: {
            mode,
            primary: {
              main: '#d7ff32',
              contrastText: '#252627'
            },
            secondary: {
              main: mode === 'dark' ? '#c5c5c7' : '#646568'
            },
            info: {
              main: mode === 'dark' ? '#c5c5c7' : '#646568'
            },
            background: {
            default: mode === 'dark' ? '#242526' : '#f4f4f4',
            paper: mode === 'dark' ? '#49494b' : '#ffffff'
          },
          text: {
            primary: mode === 'dark' ? '#f1f1f1' : '#292a2b',
            secondary: mode === 'dark' ? '#c5c5c7' : '#646568'
          }
        }
      }),
    [baseTheme, mode]
  );

  const setMode = (nextMode: CockpitThemeMode): void => {
    localStorage.setItem('cockpitTheme', nextMode);
    setModeState(nextMode);
  };

  useEffect(() => {
    document.documentElement.dataset.cockpitTheme = mode;
  }, [mode]);

  const contextValue = useMemo(
    () => ({
      mode,
      setMode,
      toggleMode: () => setMode(mode === 'dark' ? 'light' : 'dark')
    }),
    [mode]
  );

  return (
    <StylesProvider injectFirst>
      <ThemeContext.Provider value={contextValue}>
        <ThemeProvider theme={theme}>{props.children}</ThemeProvider>
      </ThemeContext.Provider>
    </StylesProvider>
  );
};

export default ThemeProviderWrapper;
